import type { BlogPostMeta } from '../types';
import { BlogPostLayout, type FaqItem, type HowToStep } from '../BlogPostLayout';
import {
  TldrCallout,
  Toc,
  FaqSection,
  InlineCta,
  InlineImage,
  CtaCard,
  PostSection,
} from '../helpers';

export const meta: BlogPostMeta = {
  slug: 'how-to-write-a-novel-outline-template',
  title: 'How to Write a Novel Outline in 2026 (with Free Template)',
  description:
    'Build a complete novel outline in one sitting. The 7-step playbook, four outline methods compared (3-act, Save the Cat, Snowflake, Hero’s Journey), and a free template.',
  excerpt:
    'A novel outline is a chapter-level map of your story before you draft a word. Here is the 7-step playbook, the four methods worth knowing, and a template you can copy in 60 seconds.',
  category: 'Craft',
  tags: ['Outlining', 'Novel Writing', 'Craft', 'Plotting', 'Story Structure'],
  author: { name: 'StoryMint Team', role: 'StoryMint Editorial' },
  coverImage: '/og/how-to-write-a-novel-outline-template.png',
  readingTime: '14 min read',
  publishedAt: '2026-05-11',
  canonicalUrl: 'https://storymint.io/blog/how-to-write-a-novel-outline-template',
};

const TOC = [
  { id: 'what-is-an-outline', label: 'What is a novel outline?' },
  { id: 'plotter-vs-pantser', label: 'Plotter vs pantser: who needs an outline?' },
  { id: 'four-methods', label: 'The four outline methods worth knowing' },
  { id: 'method-three-act', label: ' · Three-act structure' },
  { id: 'method-save-the-cat', label: ' · Save the Cat (15 beats)' },
  { id: 'method-snowflake', label: ' · Snowflake Method' },
  { id: 'method-heros-journey', label: ' · Hero’s Journey' },
  { id: 'method-comparison', label: 'Outline methods compared' },
  { id: 'seven-steps', label: 'How to write your novel outline in 7 steps' },
  { id: 'how-long', label: 'How long should a novel outline be?' },
  { id: 'common-mistakes', label: 'Five outlining mistakes to avoid' },
  { id: 'free-template', label: 'The free novel outline template' },
  { id: 'faq', label: 'FAQ' },
];

const HOWTO_STEPS: HowToStep[] = [
  {
    name: 'Capture your premise in one sentence',
    text: 'Write your story as one sentence with a protagonist, a desire, and the obstacle in the way. This sentence is the gravity well every later decision orbits.',
  },
  {
    name: 'Identify your three act anchors',
    text: 'Find the three turning points: the moment the story starts (inciting incident), the moment of no return at roughly the 50 percent mark, and the moment everything hinges on at roughly the 75 to 80 percent mark.',
  },
  {
    name: 'Map the major beats between anchors',
    text: 'List 10 to 20 beats that bridge your three act anchors. A beat is a scene-sized event that changes the protagonist’s situation or knowledge. Keep each beat to one sentence.',
  },
  {
    name: 'Break each act into chapters',
    text: 'Cluster your beats into chapter groups, aiming for 15 to 25 chapters total for a 70,000 to 90,000 word novel. Each chapter contains 1 to 4 beats and ends on a question, choice, or shift that pulls the reader forward.',
  },
  {
    name: 'Note your open plot threads',
    text: 'In a separate column, list every unresolved question your story is asking: the mystery, the romance, the political subplot. Mark each thread’s status (introduced, complicating, resolved) per chapter so nothing gets dropped.',
  },
  {
    name: 'Test the outline against your premise',
    text: 'Read your one-sentence premise, then read your outline straight through. Every chapter should either advance the obstacle, raise the stakes, or reveal something new about the protagonist’s desire. Cut chapters that do none.',
  },
  {
    name: 'Hold the outline loosely',
    text: 'Your outline is a map, not a contract. Expect roughly 30 percent of it to change during drafting. Update it as you discover better moves, but never abandon it entirely without consciously deciding to.',
  },
];

const FAQ: FaqItem[] = [
  {
    question: 'How long should a novel outline be?',
    answer:
      'For a first novel, aim for 2 to 5 pages: roughly 800 to 2,000 words, covering all three acts, every major beat, and a chapter-by-chapter line for the first 5 to 10 chapters. Longer outlines are fine but past 10 pages you are writing the book in outline form, which often kills the urgency to actually draft it. Shorter outlines (a one-page beat sheet) work for experienced writers who hold structure intuitively but are risky for a first novel.',
  },
  {
    question: 'Should I outline before I write chapter 1?',
    answer:
      'For your first novel, yes. The ten to twenty hours you spend outlining saves you the three months of writing yourself into a corner. Even pantsers (writers who draft without an outline) concede this for first novels. You can outline lightly (a one-page beat sheet) or heavily (chapter-by-chapter), but having some structural map before chapter 1 is the single highest-leverage thing you can do to finish your novel.',
  },
  {
    question: 'Plotter vs pantser: which one needs an outline?',
    answer:
      'Both need structure, but only plotters need it written down before drafting. Plotters outline first because they want to know where they are going. Pantsers draft to discover, then often outline retroactively to find the structure they wrote into. For a first novel, plotting reduces the risk of an unsalvageable draft. Past three or four novels, you can experiment with pantsing safely because you have internalized structure.',
  },
  {
    question: 'Can AI write a novel outline for me?',
    answer:
      'Yes. Tools like StoryMint generate a full chapter-by-chapter outline from a one-paragraph premise in about 30 seconds, including beat-level detail and character motivation per chapter. The trade-off is that the outline reflects the AI’s sense of story conventions, not yours. The strongest workflow is to have AI produce a first-draft outline, then edit it heavily to inject your specific surprises, themes, and voice before drafting from it.',
  },
  {
    question: 'How long does outlining a novel take?',
    answer:
      'Plan on 10 to 20 hours of focused work for a first novel outline, spread across a week or two so your subconscious can stress-test the structure between sessions. Experienced authors can outline in 3 to 5 hours because the methods are second nature. AI-generated outlines arrive in under a minute but still need a human editing pass that takes an hour or two if you want the final outline to reflect your taste.',
  },
];

export default function Post() {
  return (
    <BlogPostLayout
      meta={meta}
      howTo={{ name: meta.title, steps: HOWTO_STEPS }}
      faq={FAQ}
    >
      <TldrCallout>
        <p>
          A novel outline is a chapter-level map of your story written before you draft a
          single scene. It lists every major beat, every act break, every open plot thread,
          and the order they unfold. For a first novel, aim for a 2 to 5 page outline using
          one of four proven methods: three-act structure, Save the Cat, the Snowflake
          Method, or the Hero’s Journey.
        </p>
        <p>
          Outlining feels slow until you realise it is the fastest way to finish a novel.
          Skip the ten or twenty hours of planning and you usually pay it back with months
          of dead-end drafting. Read on for the 7-step playbook, a side-by-side method
          comparison, and a free template you can copy in 60 seconds.
        </p>
      </TldrCallout>

      <Toc entries={TOC} />

      <PostSection id="what-is-an-outline" title="What is a novel outline?">
        <p className="text-muted-foreground">
          A <strong className="text-foreground">novel outline</strong> is a structured plan
          for your story written before you draft it, listing each chapter’s major
          events, the protagonist’s arc, and how every subplot resolves. It is not a
          summary of a finished book. It is the scaffolding you build before pouring the
          concrete.
        </p>
        <p className="text-muted-foreground">
          The shortest useful outline is a <strong className="text-foreground">one-page
          beat sheet</strong>: 15 to 25 numbered story beats with one sentence each. The
          longest useful outline is a <strong className="text-foreground">chapter-by-chapter
          breakdown</strong> with 3 to 5 scenes per chapter, including character goals and
          turning points. Anything past that is no longer an outline; it is a synopsis or
          a first draft pretending to be planning.
        </p>
        <p className="text-muted-foreground">
          Outlines serve three jobs at once. They prevent structural mistakes you would
          otherwise discover at chapter 14. They make drafting faster because you wake up
          knowing what scene comes next. And they give your subconscious a stress-test
          environment: better to find the broken plot beat in the outline than 30,000 words
          into a draft.
        </p>
      </PostSection>

      <PostSection id="plotter-vs-pantser" title="Plotter vs pantser: who needs an outline?">
        <p className="text-muted-foreground">
          The eternal writing-Twitter argument splits authors into two camps.{' '}
          <strong className="text-foreground">Plotters</strong> outline everything before
          drafting.{' '}
          <strong className="text-foreground">Pantsers</strong> draft by the seat of their
          pants, discovering the story as they go.
        </p>
        <p className="text-muted-foreground">
          The honest truth is that this is a false binary. Every novel that gets finished
          has a structure underneath it. The question is just whether you write the structure
          down before, during, or after drafting. For a <em>first novel</em>, writing it
          down beforehand reduces your risk by an order of magnitude. Stephen King famously
          drafts without an outline, but Stephen King has written 60+ novels and absorbed
          story structure the way most people absorb their native language. You probably
          have not.
        </p>
        <p className="text-muted-foreground">
          A useful rule of thumb: outline tightly for your first three novels, then
          experiment with looser outlines once you can feel structure intuitively. Even
          experienced pantsers usually use a one-page beat sheet as a backstop.
        </p>
        <p className="text-muted-foreground">
          There is also a third camp worth knowing about: <strong className="text-foreground">plantsers</strong>.
          They outline the first 5 chapters tightly, draft those, then re-outline the next
          act based on what the draft revealed, then draft those, and repeat. For genre
          fiction with strong structural conventions (romance, thriller, cozy mystery)
          this hybrid often produces the best balance of structural integrity and the
          discovery energy that makes a draft feel alive.
        </p>
      </PostSection>

      <PostSection id="four-methods" title="The four outline methods worth knowing">
        <p className="text-muted-foreground">
          There are 50 outlining systems sold by 50 different writing gurus. You need to
          know four of them. Mix and match later; for your first novel, pick one and finish
          an outline before you start swapping methods.
        </p>

        <InlineImage
          src="/og/how-to-write-a-novel-outline-three-act-curve.png"
          alt="Hand drawing a three-act story arc curve in violet ink on cream paper, with vertical lines marking act breaks and a brass fountain pen resting at the page edge."
        />

        <h3
          id="method-three-act"
          className="text-xl font-semibold text-foreground mt-8 mb-3 scroll-mt-6"
          style={{ scrollMarginTop: '24px' }}
        >
          Three-act structure
        </h3>
        <p className="text-muted-foreground">
          The grandfather of all outlines. Act 1 sets up the world, character, and want
          (about 25 percent of the book). Act 2 piles on complications until the protagonist
          is at their lowest (50 percent). Act 3 forces a final choice and resolution
          (25 percent). It is the most flexible method and works for every genre. If you
          have never outlined before, start here.
        </p>
        <p className="text-muted-foreground">
          The two hinge moments to nail are the{' '}
          <strong className="text-foreground">midpoint</strong> (around 50 percent: a
          revelation or reversal that recommits the protagonist), and the{' '}
          <strong className="text-foreground">all-is-lost</strong> moment (around 75
          percent: the protagonist seems to have failed). Get those two right and the rest
          of the structure tends to fall into place.
        </p>

        <h3
          id="method-save-the-cat"
          className="text-xl font-semibold text-foreground mt-8 mb-3"
          style={{ scrollMarginTop: '24px' }}
        >
          Save the Cat (15 beats)
        </h3>
        <p className="text-muted-foreground">
          Blake Snyder’s 15-beat sheet, originally for screenwriters, ported widely to
          novelists by Jessica Brody’s adaptation in{' '}
          <a
            href="https://savethecat.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Save the Cat! Writes a Novel
          </a>
          . You hit 15 specific beats with target page or word percentages: Opening Image,
          Theme Stated, Setup, Catalyst, Debate, Break into Two, B Story, Fun and Games,
          Midpoint, Bad Guys Close In, All Is Lost, Dark Night of the Soul, Break into
          Three, Finale, Final Image.
        </p>
        <p className="text-muted-foreground">
          Save the Cat is the most prescriptive of the four. That is either a feature
          (commercial fiction with built-in pacing) or a bug (it can feel formulaic if you
          treat the beats as a checklist rather than waypoints). It excels for thriller,
          romance, and other genres where readers expect a familiar rhythm.
        </p>
        <p className="text-muted-foreground">
          The beat to obsess over is the <strong className="text-foreground">Midpoint</strong>,
          at roughly the 50 percent mark. Most failing manuscripts have a weak midpoint
          where the protagonist is reacting to events rather than driving them. Save the
          Cat forces you to plant a revelation or reversal at exactly that point that
          recommits the protagonist to the goal. Nail the Midpoint and Acts 2A and 2B
          almost outline themselves around it.
        </p>

        <h3
          id="method-snowflake"
          className="text-xl font-semibold text-foreground mt-8 mb-3"
          style={{ scrollMarginTop: '24px' }}
        >
          The Snowflake Method
        </h3>
        <p className="text-muted-foreground">
          Created by{' '}
          <a
            href="https://www.advancedfictionwriting.com/articles/snowflake-method/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Randy Ingermanson
          </a>
          , the Snowflake Method is iterative expansion. You start with a one-sentence
          summary, expand it to a one-paragraph summary, expand each paragraph sentence to
          a paragraph of its own, expand each character to a one-page bio, and so on until
          you have a 50-page outline.
        </p>
        <p className="text-muted-foreground">
          Snowflake works best for systematic thinkers who like resolving structure layer by
          layer. It produces the most detailed outlines of the four methods, which is great
          if you draft fast once you have a plan and slow if you do not. If outlining starts
          to feel like drafting, you have probably gone one Snowflake level too deep.
        </p>
        <p className="text-muted-foreground">
          The biggest risk with Snowflake is what writers half-jokingly call paralysis by
          Snowflake. Each expansion layer feels like productive planning, so you keep going.
          Hard rule: once you have a four-page summary plus chapter-level beats, stop. Past
          that, you are drafting the book inside a planning document and burning the energy
          you should be spending on the real draft.
        </p>

        <h3
          id="method-heros-journey"
          className="text-xl font-semibold text-foreground mt-8 mb-3"
          style={{ scrollMarginTop: '24px' }}
        >
          The Hero’s Journey
        </h3>
        <p className="text-muted-foreground">
          Joseph Campbell’s comparative mythology framework, simplified by Christopher
          Vogler for storytellers. The protagonist crosses 12 stages: Ordinary World, Call
          to Adventure, Refusal, Mentor, Crossing the Threshold, Tests and Allies, Approach,
          Ordeal, Reward, Road Back, Resurrection, Return with the Elixir.
        </p>
        <p className="text-muted-foreground">
          Hero’s Journey is built for fantasy, adventure, sci-fi, and any story with a
          clear transformation arc. It is overkill for quiet literary fiction. Use it when
          your protagonist needs to physically or psychologically <em>change</em> in a way
          you can chart against a clear before-and-after.
        </p>
      </PostSection>

      <PostSection id="method-comparison" title="Outline methods compared">
        <p className="text-muted-foreground">
          Four methods, four use cases, four trade-offs. Pick by what you are writing and
          how you think, not by what is fashionable on writing-Twitter this month.
        </p>

        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-semibold text-foreground">Method</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Best for</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Detail level</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground">Learning curve</th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-b border-border">
                <td className="py-3 px-4 align-top">
                  <strong className="text-foreground">Three-act</strong>
                </td>
                <td className="py-3 px-4 align-top">Any genre, first-time outliners</td>
                <td className="py-3 px-4 align-top">Low to medium</td>
                <td className="py-3 px-4 align-top">Hours</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 align-top">
                  <strong className="text-foreground">Save the Cat</strong>
                </td>
                <td className="py-3 px-4 align-top">Commercial fiction, thriller, romance</td>
                <td className="py-3 px-4 align-top">Medium</td>
                <td className="py-3 px-4 align-top">A day or two</td>
              </tr>
              <tr className="border-b border-border">
                <td className="py-3 px-4 align-top">
                  <strong className="text-foreground">Snowflake</strong>
                </td>
                <td className="py-3 px-4 align-top">Systematic planners, mystery, sci-fi</td>
                <td className="py-3 px-4 align-top">High</td>
                <td className="py-3 px-4 align-top">A week, then second nature</td>
              </tr>
              <tr>
                <td className="py-3 px-4 align-top">
                  <strong className="text-foreground">Hero’s Journey</strong>
                </td>
                <td className="py-3 px-4 align-top">Fantasy, adventure, transformation arcs</td>
                <td className="py-3 px-4 align-top">Medium</td>
                <td className="py-3 px-4 align-top">A day or two</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-muted-foreground">
          A practical recommendation if you cannot decide: outline your first novel with
          three-act structure. It is genre-agnostic, low-friction, and gives you the
          fundamentals that every other method extends. Add a Save the Cat or Hero’s
          Journey overlay on book two if you want more structure.
        </p>
      </PostSection>

      <PostSection id="seven-steps" title="How to write your novel outline in 7 steps">
        <p className="text-muted-foreground">
          Method picked. Now the actual sit-down work. This is the playbook regardless of
          which framework you chose; the only thing that changes between methods is what
          you call the beats and what targets you hit for each one.
        </p>

        <InlineImage
          src="/og/how-to-write-a-novel-outline-corkboard.png"
          alt="Top-down view of a wall covered with rows of white index cards pinned in three horizontal bands, representing the beats of a novel arranged by act."
        />

        <h3 className="text-lg font-semibold text-foreground mt-8 mb-2">
          1. Capture your premise in one sentence
        </h3>
        <p className="text-muted-foreground">
          Write your story as one sentence: <em>A [who] wants [what], but [obstacle]</em>.
          If you cannot fill in all three blanks, you do not have a premise yet. This
          sentence is the gravity well every later decision orbits. Pin it at the top of
          your outline document and re-read it every time you sit down to plan.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          2. Identify your three act anchors
        </h3>
        <p className="text-muted-foreground">
          Find your three biggest turning points: the{' '}
          <strong className="text-foreground">inciting incident</strong> (Act 1 closes,
          around 25 percent), the <strong className="text-foreground">midpoint</strong>{' '}
          (around 50 percent: a revelation that recommits the protagonist), and the{' '}
          <strong className="text-foreground">all-is-lost</strong> moment (around 75
          percent: protagonist seems to have failed). These three are your skeleton.
          Everything else is muscle and skin around them.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          3. Map the major beats between anchors
        </h3>
        <p className="text-muted-foreground">
          Between your three anchors, fill in 10 to 20 beats. A beat is a scene-sized event
          that changes the protagonist’s situation or knowledge. Keep each beat to one
          sentence. If you cannot reduce a beat to one sentence, it is two beats stacked.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          4. Break each act into chapters
        </h3>
        <p className="text-muted-foreground">
          Cluster your beats into chapter groups. Aim for 15 to 25 chapters total for a
          70,000 to 90,000 word novel. Each chapter contains 1 to 4 beats and ends on a
          question, a choice, or a status shift that pulls the reader forward into the next
          chapter.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          5. Note your open plot threads
        </h3>
        <p className="text-muted-foreground">
          Create a separate column for plot threads: every unresolved question your story
          is asking. The mystery, the romance, the political subplot, the family secret.
          Each thread gets a row with its status (introduced, complicating, resolved) per
          chapter. This is the single best safeguard against forgetting a subplot in
          chapter 18.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          6. Test the outline against your premise
        </h3>
        <p className="text-muted-foreground">
          Read your one-sentence premise. Then read the outline straight through. Every
          chapter should either{' '}
          <strong className="text-foreground">advance the obstacle</strong>,{' '}
          <strong className="text-foreground">raise the stakes</strong>, or{' '}
          <strong className="text-foreground">reveal something new</strong> about the
          protagonist’s desire. Cut any chapter that does none of those. Cutting in
          the outline costs minutes; cutting in the draft costs weeks.
        </p>

        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">
          7. Hold the outline loosely
        </h3>
        <p className="text-muted-foreground">
          Your outline is a map, not a contract. Expect roughly 30 percent of it to change
          during drafting as you discover better moves. Update it as you go, but never
          abandon it entirely without consciously deciding to. The outline’s job is to
          be a fallback when you get lost, not a cage that prevents you from finding
          something better.
        </p>
      </PostSection>

      <PostSection id="how-long" title="How long should a novel outline be?">
        <p className="text-muted-foreground">
          For a first novel: <strong className="text-foreground">2 to 5 pages, roughly
          800 to 2,000 words</strong>. Covers all three acts, every major beat, and a
          chapter-by-chapter line for at least the first half of the book. Past 10 pages,
          you are writing the book in outline form, which often kills the urgency to draft
          the actual book.
        </p>
        <p className="text-muted-foreground">
          For experienced novelists, a one-page beat sheet (15 to 20 lines) is usually
          enough. They have internalized structure and use the outline as a memory aid, not
          a planning tool.
        </p>
        <p className="text-muted-foreground">
          The wrong answer is anything you wrote in one sitting and never revised. Plan on
          three or four passes through the outline before you draft chapter 1. Each pass
          surfaces a problem you would otherwise hit at chapter 14.
        </p>
      </PostSection>

      <PostSection id="common-mistakes" title="Five outlining mistakes to avoid">
        <p className="text-muted-foreground">
          Every common outlining failure has the same root cause: confusing the outline
          with the book. Watch for these five and your draft will go a lot smoother.
        </p>
        <ol className="list-decimal pl-6 space-y-3 text-muted-foreground">
          <li>
            <strong className="text-foreground">Outlining past the point of usefulness.</strong>{' '}
            When your outline crosses 5,000 words, you are no longer outlining. You are
            writing the book in outline form. The energy you would have spent drafting
            bleeds out into a planning document nobody will ever read. Cap a first-novel
            outline at 2,500 words and force yourself into chapter 1.
          </li>
          <li>
            <strong className="text-foreground">Refusing to deviate mid-draft.</strong> Outlines
            are maps, not contracts. If chapter 9 reveals a better path than the one you
            outlined for chapter 10, take it. The drafted version usually beats the planned
            version because drafting surfaces information your planning brain did not have
            access to. Update the outline as you go; do not let it cage you.
          </li>
          <li>
            <strong className="text-foreground">Forgetting subplots after introducing them.</strong>{' '}
            Subplots that get a strong setup in chapter 3 and then vanish until chapter 22
            are the single most common feedback note from beta readers. Your plot-thread
            column (Step 5) is the fix. Scan it every five chapters and make sure no thread
            has gone silent for too long.
          </li>
          <li>
            <strong className="text-foreground">Outlining only the plot.</strong> A complete
            outline includes character interiority, not just events. After each chapter,
            write one sentence: <em>By the end of this chapter, the protagonist now believes
            (or wants, or fears) [X]</em>. If the character ends every chapter believing
            the same thing they started with, no arc is happening and your plot is doing
            all the work alone.
          </li>
          <li>
            <strong className="text-foreground">Treating the outline as one-and-done.</strong>{' '}
            Plan on three or four revision passes through the outline before you draft
            chapter 1, and continuous revision during drafting. The outline that goes into
            chapter 1 is rarely the outline that comes out of chapter 25, and that is
            normal. Static outlines mean either you have a perfect first draft (you do
            not) or you stopped paying attention to your own story.
          </li>
        </ol>
      </PostSection>

      <InlineCta
        eyebrow="If 20 hours of outlining feels like a lot"
        heading="Or skip to step 7 and generate one in 30 seconds"
        body="StoryMint takes a one-paragraph premise and produces a complete chapter-by-chapter outline (15 to 25 chapters, with beat-level detail and character motivation) in under a minute. You stay in the editor’s chair. The catch is the same as always: it is a first draft of an outline, so you edit before drafting from it. Three chapters free, no credit card."
        buttonLabel="Generate an outline free →"
        buttonHref="/signup"
      />

      <PostSection id="free-template" title="The free novel outline template">
        <p className="text-muted-foreground">
          Copy this into a Google Doc or Notion page and fill it in. It is the
          three-act-with-beats version, which works for any genre. Aim to keep the entire
          template under 2,000 words once filled.
        </p>

        <InlineImage
          src="/og/how-to-write-a-novel-outline-template-pages.png"
          alt="Close-up of an open leather notebook with handwritten chapter beats in violet ink, beside a small stack of plotting index cards under a brass desk lamp."
        />

        <div className="rounded-xl border border-border bg-card p-6 my-6 text-sm">
          <pre className="whitespace-pre-wrap font-mono text-[12.5px] leading-[1.7] text-muted-foreground">
{`PREMISE (1 sentence)
A [who] wants [what], but [obstacle].

GENRE / SUBGENRE
[Adult contemporary romance / cozy mystery / dark fantasy / etc.]

TARGET LENGTH
[70,000 - 90,000 words]

CAST (3 lines per major character)
- [Name]: [age, defining flaw, what they want]
- [Name]: [age, defining flaw, what they want]
- [Name]: [age, defining flaw, what they want]

──────────────────────────────
ACT 1 (chapters 1 - 5, roughly 25%)
──────────────────────────────
• Opening: [the world before the story]
• Inciting incident (~10%): [the thing that breaks the world]
• Debate (~15-20%): [why protagonist resists answering the call]
• Break into Act 2 (~25%): [protagonist commits to the goal]

ACT 2A (chapters 6 - 12, roughly 25 - 50%)
──────────────────────────────
• New world: [protagonist explores the goal]
• Allies and enemies: [supporting cast asserts itself]
• Subplot kickoff: [the B story begins]
• Midpoint (~50%): [a reversal that recommits or shifts the protagonist]

ACT 2B (chapters 13 - 18, roughly 50 - 75%)
──────────────────────────────
• Bad guys close in: [opposition tightens]
• Subplot complicates: [B story crosses A story]
• All is lost (~75%): [worst-case moment for the protagonist]
• Dark night of the soul: [protagonist reconsiders everything]

ACT 3 (chapters 19 - 22, roughly 75 - 100%)
──────────────────────────────
• New plan: [protagonist gets up off the floor]
• Climax: [the final confrontation]
• Resolution: [the new normal]
• Final image: [the mirror to the opening]

──────────────────────────────
OPEN PLOT THREADS (track per chapter)
──────────────────────────────
[Thread 1]: introduced ch __ → complicates ch __ → resolves ch __
[Thread 2]: introduced ch __ → complicates ch __ → resolves ch __
[Thread 3]: introduced ch __ → complicates ch __ → resolves ch __`}
          </pre>
        </div>

        <p className="text-muted-foreground">
          Fill this in, run it through the 7-step playbook above, and you have an outline
          ready to draft from. If you want this same template generated automatically from
          your premise, plus character bios and chapter-level scene beats,{' '}
          <a href="/signup" className="text-primary hover:underline">
            try a free chapter with StoryMint
          </a>{' '}
          and see how it lays out your story.
        </p>
      </PostSection>

      <FaqSection items={FAQ} />

      <CtaCard
        heading="Finish the outline. Then finish the book."
        body="StoryMint generates a complete chapter-by-chapter outline from your premise in under a minute, then writes the first draft from it. You stay in the editor’s chair. Three free chapters, no credit card."
        buttonLabel="Try 3 chapters free →"
        buttonHref="/signup"
      />
    </BlogPostLayout>
  );
}
